import React, { useState } from "react";
import "@/App.css";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/game/auth";
import { useGame, INITIAL_STATE } from "@/game/store";
import { audio } from "@/game/audio";
import { Atmosphere } from "@/components/game/Atmosphere";
import { MainMenu } from "@/components/game/MainMenu";
import { GameScene } from "@/components/game/GameScene";
import { HallucinationOverlay } from "@/components/game/HallucinationOverlay";
import { AboutScreen } from "@/components/game/AboutScreen";
import { LoginScreen } from "@/components/game/LoginScreen";
import { HowToPlay } from "@/components/game/HowToPlay";

const TOAST_OPTIONS = {
  style: { background: "#080808", border: "1px solid rgba(139,0,0,0.4)", color: "#E0E0E0", fontFamily: "Special Elite" },
};

const HTP_SEEN_KEY = "darkrequiem_htp_seen";

function Game() {
  const auth = useAuth();
  const { state, meta, loaded, choose, whisper, dismissWhisper, resetGame, triggerHallucination, playMemory } = useGame();
  const [screen, setScreen] = useState(() => {
    // Show tutorial on first-ever visit for this browser
    return localStorage.getItem(HTP_SEEN_KEY) === "1" ? "menu" : "howto";
  });

  const hasSave = loaded && state.scene_id !== INITIAL_STATE.scene_id;

  const startNew = () => {
    audio.unlock();
    audio.startAmbient("exploration");
    resetGame();
    setScreen("game");
  };
  const continueGame = () => {
    audio.unlock();
    audio.startAmbient("exploration");
    setScreen("game");
  };
  const openCredits = () => {
    audio.unlock();
    audio.startAmbient("credits");
    setScreen("about");
  };
  const openHowTo = () => {
    audio.unlock();
    setScreen("howto");
  };
  const closeHowTo = () => {
    localStorage.setItem(HTP_SEEN_KEY, "1");
    setScreen("menu");
  };

  return (
    <div className="App font-sans">
      <Atmosphere />
      {screen === "menu" && (
        <MainMenu
          hasSave={hasSave}
          meta={meta}
          auth={auth}
          onStart={startNew}
          onContinue={continueGame}
          onCredits={openCredits}
          onHowToPlay={openHowTo}
        />
      )}
      {screen === "howto" && <HowToPlay onClose={closeHowTo} />}
      {screen === "game" && (
        <GameScene
          state={state}
          auth={auth}
          onChoose={choose}
          onExit={() => setScreen("menu")}
          onSummonWhisper={triggerHallucination}
          onPlayMemory={playMemory}
        />
      )}
      {screen === "about" && <AboutScreen onBack={() => { audio.startAmbient("exploration"); setScreen("menu"); }} />}
      <HallucinationOverlay whisper={whisper} onDismiss={dismissWhisper} />
      <Toaster theme="dark" position="bottom-right" toastOptions={TOAST_OPTIONS} />
    </div>
  );
}

function Gate() {
  const auth = useAuth();
  if (auth.loading) {
    return (
      <div className="App font-sans">
        <Atmosphere />
        <div className="relative min-h-screen flex items-center justify-center" data-testid="app-loading">
          <p className="font-mono text-xs tracking-[0.4em] uppercase text-white/40 animate-flicker">
            // checking session ...
          </p>
        </div>
      </div>
    );
  }
  if (!auth.user && !auth.guest) return <><Atmosphere /><LoginScreen /></>;
  return <Game />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
